import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchHistoryUtil } from '../../utils/searchHistory';

export default function SearchHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = () => {
    const historyData = searchHistoryUtil.getHistory();
    setHistory(historyData);
  };

  const handleDelete = (id) => {
    searchHistoryUtil.deleteSearch(id);
    loadHistory();
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all search history?')) {
      searchHistoryUtil.clearAll();
      loadHistory();
    }
  };

  const isAnalysisResult = (item) => {
    return item.query && item.query.includes('Patent Review Analysis');
  };

  const handleViewResults = (item) => {
    if (isAnalysisResult(item)) {
      navigate(`/analyze/results?result_id=${item.resultId}&asin=${item.asin}`);
    } else {
      let url = `/search?id=${item.resultId}&query=${encodeURIComponent(item.query)}`;
      if (item.productTitle) {
        url += `&productTitle=${encodeURIComponent(item.productTitle)}`;
      }
      if (item.productImageUrl) {
        url += `&productImageUrl=${encodeURIComponent(item.productImageUrl)}`;
      }
      navigate(url);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const getSearchTypeLabel = (item) => {
    if (isAnalysisResult(item)) return 'Analysis';
    if (item.asin) return 'ASIN';
    if (item.title) return 'Manual Input';
    return 'Plain Search';
  };

  return (
    <div className="flex-1 px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Search History</h1>
            <p className="text-gray-300">View and manage your patent search history</p>
          </div>
          {history.length > 0 && (
            <button
              onClick={handleClearAll}
              className="px-4 py-2 bg-red-600/80 hover:bg-red-600 text-white rounded-xl font-semibold transition-all"
            >
              Clear All
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12 text-center">
            <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-semibold text-white mb-2">No Search History</h2>
            <p className="text-gray-400 mb-6">Your search history will appear here after you perform searches</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-semibold transition-all"
            >
              Start Searching
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((item) => (
              <div
                key={item.id}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  {item.asin && item.productImageUrl && (
                    <div className="flex-shrink-0 w-24 h-24 bg-gray-800 rounded-xl overflow-hidden">
                      <img
                        src={item.productImageUrl}
                        alt={item.productTitle || 'Product'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className={`inline-block px-3 py-1 text-white text-xs font-semibold rounded-lg ${
                        isAnalysisResult(item) 
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600' 
                          : 'bg-purple-600/80'
                      }`}>
                        {getSearchTypeLabel(item)}
                      </span>
                      <span className="text-sm text-gray-400">
                        {formatDate(item.timestamp)}
                      </span>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {item.query}
                      </h3>
                      {item.asin && (
                        <>
                          <p className="text-sm text-gray-400">
                            ASIN: <span className="text-gray-300 font-mono">{item.asin}</span>
                          </p>
                          {item.productTitle && (
                            <p className="text-sm text-gray-400 mt-1">
                              Product: <span className="text-gray-300">{item.productTitle}</span>
                            </p>
                          )}
                        </>
                      )}
                      {item.title && (
                        <p className="text-sm text-gray-400">
                          Title: <span className="text-gray-300">{item.title}</span>
                        </p>
                      )}
                      {item.description && (
                        <p className="text-sm text-gray-400 line-clamp-2">
                          Description: <span className="text-gray-300">{item.description}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewResults(item)}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-semibold transition-all text-sm"
                    >
                      {isAnalysisResult(item) ? 'View Analysis' : 'View Results'}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 bg-red-600/80 hover:bg-red-600 text-white rounded-xl transition-all"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

